import type { JSX } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  getQueryTextForSearch,
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuResolution,
} from "../type-ahead/type-ahead-plugin";
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  BLUR_COMMAND,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_DOWN_COMMAND,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  LexicalNode,
  TextNode,
} from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as ReactDOM from "react-dom";
import {
  MenuBox,
  MenuBoxOptionBase,
  MenuBoxProps,
  MenuBoxRenderItem,
} from "./menu-box";
import last from "lodash/last";
import { useMentionLookupService } from "./use-mention-lookup-service";
import { useBasicTypeaheadTriggerMatchV2 } from "./type-ahead-match";
import { MentionPluginInstance, MentionsPluginProps } from "./types";
import type { TextPoint as TextPointType } from "lexical";
import { mergeRegister } from "../editor-utils";

const DEFAULT_LOOKUP_CONFIG = {};

class MentionTypeaheadOption<T extends MenuBoxOptionBase> extends MenuOption {
  public object: T;
  constructor(object: T) {
    super(object.key);
    this.object = object;
  }
}

export function MentionsPlugin<T extends MenuBoxOptionBase>({
  pluginRef,
  lookupConfig: {
    trigger = "@",
    minLength = 0,
    maxLength = 100,
    allowWhitespace = true,
    typeDebounceTime = 300,
    cache,
    punctuation,
  } = DEFAULT_LOOKUP_CONFIG,
  menuBoxBehavior,
  disabled,
  onSelectItem,
}: MentionsPluginProps<T>): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const isInLookupContextRef = useRef(false);
  const resolvedIsSearchConditionMet = menuBoxBehavior.isSearchConditionMet
    ? menuBoxBehavior.isSearchConditionMet
    : () => ({ status: "success" }) as const;

  const {
    data: results,
    isLoading: isRequesting,
    error,
    reload,
  } = useMentionLookupService({
    rawMention: queryString,
    isSearchConditionMet: resolvedIsSearchConditionMet,
    typeDebounceTime: typeDebounceTime || 300,
    dataFetcher: menuBoxBehavior.dataFetcher,
    cache,
    dataResolver: menuBoxBehavior.dataResolver,
  });
  const checkForAtTriggerMatch = useBasicTypeaheadTriggerMatchV2(trigger, {
    minLength,
    maxLength,
    allowWhitespace,
    punctuation,
  });
  const cacheAnchorRectRef = useRef<DOMRect | null>(null);

  useEffect(() => {
    if (pluginRef) {
      (pluginRef as React.MutableRefObject<MentionPluginInstance>).current = {
        reload,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (disabled && isInLookupContextRef.current) {
      isInLookupContextRef.current = false;
      setQueryString(null);
      cacheAnchorRectRef.current = null;
    }
  }, [disabled]);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        BLUR_COMMAND,
        () => {
          if (isInLookupContextRef.current) {
            /** allow for other events (e.g., click) to be processed */
            setTimeout(() => {
              if (isInLookupContextRef.current) {
                isInLookupContextRef.current = false;
                setQueryString(null);
              }
            }, 50);
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isInLookupContextRef.current) {
            isInLookupContextRef.current = false;
            setQueryString(null);
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (e) => {
          const lastTriggerChar = last(trigger);
          if (e.key === lastTriggerChar && !isInLookupContextRef.current) {
            const text = getQueryTextForSearch(editor) || "";
            if (checkForAtTriggerMatch(`${text}${lastTriggerChar}a`, editor)) {
              isInLookupContextRef.current = true;
              setQueryString("");
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, trigger]);
  const detectMatch = useCallback(
    (text: string, editor: LexicalEditor) => {
      if (!isInLookupContextRef.current) {
        return null;
      }

      if (text.endsWith(trigger)) {
        return {
          leadOffset: text.length - trigger.length,
          matchingString: "",
          replaceableString: trigger,
        };
      }

      const rs = checkForAtTriggerMatch(text, editor);
      if (!rs) {
        isInLookupContextRef.current = false;
      }
      return rs;
    },
    [checkForAtTriggerMatch, trigger]
  );

  const options = useMemo(() => {
    return results
      .map((result) => new MentionTypeaheadOption(result))
      .slice(0, menuBoxBehavior.maxItemsToShow || 100);
  }, [results, menuBoxBehavior.maxItemsToShow]);

  const onSelectOption = (
    selectedOption: MentionTypeaheadOption<T>,
    nodeToReplace: TextNode | null | undefined,
    closeMenu: () => void,
    _matchingString: string | null,
    match: MenuResolution["match"] | undefined
  ) => {
    editor.update(() => {
      const { nodeConversion } = menuBoxBehavior;
      nodeToReplace =
        nodeToReplace || $findNodeToReplace(trigger, match?.replaceableString);

      switch (nodeConversion.type) {
        case "plain-text": {
          if (nodeToReplace) {
            const text = nodeConversion.convertFunc(selectedOption.object);
            const textNode = $createTextNode(text);
            nodeToReplace.replace(textNode);
            if (nodeConversion.trailingSpace) {
              const spaceNode = $insertTrailingSpace(textNode);
              spaceNode.select();
            } else {
              textNode.select();
            }
          }

          break;
        }
        case "mention-node": {
          const info = nodeConversion.convertFunc(selectedOption.object);
          const mentionNode = nodeConversion.$createMentionNode(
            info.platformStructureText,
            info.displayText
          );

          if (nodeToReplace) {
            nodeToReplace.replace(mentionNode);
            if (nodeConversion.trailingSpace) {
              $insertTrailingSpace(mentionNode);
            }
          }

          break;
        }
        case "custom": {
          nodeConversion.convertFunc(
            selectedOption.object,
            nodeToReplace || null
          );

          break;
        }
        // No default
      }
    });
    closeMenu();
    isInLookupContextRef.current = false;
    onSelectItem?.(selectedOption.object);
    setQueryString(null);
  };

  const conditionResult = resolvedIsSearchConditionMet(queryString);
  function resolveData() {
    if (conditionResult.status === "error") {
      return {
        type: "note",
        message: conditionResult.conditionMessage || "",
      } as const;
    }

    return {
      type: "success",
      data: options.map((c) => c.object),
    } as const;
  }
  if (disabled) {
    return null;
  }
  const showLoading = menuBoxBehavior.showLoading ?? true;

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption<T>>
      cacheAnchorRectRef={cacheAnchorRectRef}
      calculatePositionOnOptionChange
      ignoreEntityBoundary
      commandPriority={COMMAND_PRIORITY_HIGH}
      findAnchorSearchText={(editor) => {
        if (!isInLookupContextRef.current) {
          return null;
        }
        let text = null;
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return;
          }
          const anchor = selection.anchor;
          if (anchor.type !== "text") {
            return;
          }

          const result = findNodesToTest(anchor, trigger, allowWhitespace);
          text = result.text;
        });
        return text;
      }}
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={detectMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        const menuBoxProps: MenuBoxProps<T> = {
          data: resolveData(),
          selectedIndex,
          selectOptionAndCleanUp: (item) => {
            const found = options.find((c) => c.key === item.key);
            if (found) {
              selectOptionAndCleanUp(found);
            }
            setTimeout(() => {
              editor.focus();
            }, 100);
          },
          setHighlightedIndex: setHighlightedIndex,
          isLoading: conditionResult.status !== "error" && isRequesting,
          renderContentLayout: menuBoxBehavior.renderContentLayout,
          error,
          showLoading,
          renderItem: ((item: T, settings) => {
            return menuBoxBehavior.renderItem ? (
              menuBoxBehavior.renderItem(item, settings)
            ) : (
              <span>{item.key}</span>
            );
          }) satisfies MenuBoxRenderItem<T>,
        };
        return anchorElementRef.current && isInLookupContextRef.current
          ? ReactDOM.createPortal(
              menuBoxBehavior.renderMenuBox ? (
                menuBoxBehavior.renderMenuBox(menuBoxProps, {
                  lookupText: queryString,
                })
              ) : (
                <MenuBox {...menuBoxProps} />
              ),
              anchorElementRef.current
            )
          : null;
      }}
    />
  );
}

function $findNodeToReplace(
  trigger: string,
  replaceableString: string | undefined
) {
  const baseSelection = $getSelection();
  if (!$isRangeSelection(baseSelection) || !replaceableString) {
    return;
  }
  const anchor = baseSelection.anchor;
  if (anchor.type !== "text") {
    return;
  }
  const { nodes } = findNodesToTest(anchor, trigger, true);
  if (nodes.length === 0) {
    return;
  }
  const mainTextNode = joinAsTextNode(nodes);
  anchor.getNode().replace(mainTextNode);

  return findNodeMatchedText(mainTextNode, replaceableString);
}

function findNodeMatchedText(
  mainTextNode: TextNode,
  replaceableString: string | undefined
) {
  if (!replaceableString) {
    return;
  }
  const startIndex = mainTextNode.getTextContent().indexOf(replaceableString);
  if (startIndex === -1) {
    return;
  }
  if (startIndex === 0) {
    const [firstNode] = mainTextNode.splitText(replaceableString.length);
    return firstNode;
  }
  const [, middleNode] = mainTextNode.splitText(
    startIndex,
    startIndex + replaceableString.length
  );
  return middleNode;
}

function joinAsTextNode(nodes: TextNode[]) {
  const mainTextNode = $createTextNode(
    nodes.map((c) => c.getTextContent()).join("")
  );
  if (nodes.length > 1) {
    for (let i = 0; i < nodes.length - 1; i++) {
      nodes[i].remove();
    }
  }
  return mainTextNode;
}

function findNodesToTest(
  anchor: TextPointType,
  trigger: string,
  allowWhitespace: boolean
): { nodes: TextNode[]; text: string } {
  let text = "";
  const node = anchor.getNode();
  let nodes: TextNode[] = [];
  if ($isTextNode(node)) {
    text = node.getTextContent().slice(0, anchor.offset);
    nodes.push(node);
  }
  const prevNode = node.getPreviousSibling();
  if (
    $isTextNode(prevNode) &&
    !node.getTextContent().includes(trigger) &&
    allowWhitespace
  ) {
    text = prevNode.getTextContent() + text;
    nodes = [prevNode, ...nodes];
  }
  return { nodes, text };
}

function $insertTrailingSpace<T extends LexicalNode>(currentNode: T) {
  const nextNode = currentNode.getNextSibling();
  if (
    nextNode === null ||
    !$isTextNode(nextNode) ||
    !nextNode.getTextContent().startsWith(" ")
  ) {
    const spaceNode = $createTextNode(" ");
    currentNode.insertAfter(spaceNode);
    return spaceNode;
  }
  return currentNode;
}
